import clsx from 'clsx'

import type { ComponentProps, JSX } from 'react'

/**
 * The `.wrap` max-width and gutter. Measured live (`https://www.sonicjobs.com/`, 2026-09-23) by reading
 * the winning CSS rule with `document.styleSheets`, not the repo: `clients/marketing-pages/styles.css`
 * declares `.wrap { width: min(1240px, calc(100% - 96px)) }`, but that rule loses the cascade to the
 * `@openai/apps-sdk-ui` selector `body:not(.subpage) main > .wrap`, which wins on specificity with
 * `width: min(var(--oai-main-width), calc(100% - 48px))`. `--oai-main-width` computes to `1376px`, and
 * that cap is confirmed both algebraically (`min(1376, 100% - 48px)` matches the measured 1376/1142/718
 * at 1728/1200/760px, once a ~10px scrollbar is allowed for) and directly (`getComputedStyle` reads
 * `1376px` at 1728px).
 *
 * Below 760px the gutter rule flips to a smaller fixed value (`--oai-main-gutter: 16px`, rendered here
 * as `calc(100% - 32px)`) with no cap change; there is no intermediate breakpoint (checked: only two
 * `--oai-main-gutter`/`--oai-main-width` declarations exist in the deployed stylesheet, default and
 * `max-width: 760px`). `max-[761px]` is used, not `max-[760px]`, because Tailwind's `max-*` means
 * `width < N` while production's rule is `max-width: 760px` (the same off-by-one as the Button's
 * `max-[381px]`).
 *
 * Neither `1376px` nor the `24px`/`16px` gutters have a design token: they come from the Apps SDK's own
 * CSS custom properties, which this route group intentionally does not import (see button.tsx's
 * docblock). They are arbitrary values here, same as the button and chip primitives' measured pixel
 * sizes.
 *
 * `mx-auto` centres the box — production's `.wrap` still carries `margin-inline: auto` and nothing
 * overrides it (confirmed: equal left/right margins at every measured width). This is the one primitive
 * allowed to set its own layout, because centring and constraining width is its entire job.
 */
export default function Container({ className, ...props }: ComponentProps<'div'>): JSX.Element {
  return (
    <div
      {...props}
      className={clsx('mx-auto w-[calc(100%-48px)] max-w-[1376px] max-[761px]:w-[calc(100%-32px)]', className)}
    />
  )
}
